/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-named-as-default-member */

import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import ExcelJS, { Row } from 'exceljs'
import { camelCase } from 'lodash'

import { Category, Participant } from '../../../../API'
import {
  EVENT_SHIRT_DETAILS,
  JERSEY_SHIRT_SIZES,
  SHIRT_KEY,
  TEAM_TYPES,
  TSHIRT_SIZES,
} from '../../../../constants/models'
import { EventShirtDetailsProps } from '../../../../constants/types'

import {
  dataStyle,
  dateSort,
  headerStyle,
  prioritySort,
  Event,
  SpreadsheetInput,
  summaryHeaderStyle,
  summaryStyle,
  summaryFooterStyle,
  cleanName,
  addRow,
  ItemCount,
} from '.'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Manila')
dayjs.extend(isSameOrBefore)

const CONVERT_SIZE = {
  _3XS: '3XS',
  _2XS: '2XS',
  XXXS: '3XS',
  XXS: '2XS',
  _2XL: '2XL',
  _3XL: '3XL',
  _4XL: '4XL',
  XXL: '2XL',
  XXXL: '3XL',
  XXXXL: '4XL',
}

export const workbook = (event: Event, data: SpreadsheetInput) => {
  let sheet: ExcelJS.Worksheet
  const workbook = new ExcelJS.Workbook()
  const tshirtCountArr = Object.keys(TSHIRT_SIZES).reduce(
    (prev, cur, index) => ({
      ...prev,
      [cur.replace('_', '')]: {
        count: 0,
        name: cur.replace('_', ''),
        priority: index + 1,
      },
    }),
    {},
  )

  // let hasShirt2 = false
  const hasShirt2 = event.categories.items.some((i) => !!i.hasShirt2)
  const tshirt2CountArr = Object.keys(TSHIRT_SIZES).reduce(
    (prev, cur, index) => ({
      ...prev,
      [cur.replace('_', '')]: {
        count: 0,
        name: cur.replace('_', ''),
        priority: index + 1,
      },
    }),
    {},
  )

  const hasJersey = event.categories.items.some((i) => !!i.hasJersey)
  const jerseySizeArr = Object.keys(JERSEY_SHIRT_SIZES).reduce(
    (prev, cur, index) => ({
      ...prev,
      [cur.replace('_', '')]: {
        count: 0,
        name: cur.replace('_', ''),
        priority: index + 1,
      },
    }),
    {},
  )
  const categoryCountArr = event.categories.items?.reduce(
    (prev, cur) => ({
      ...prev,
      [cur.id]: {
        count: 0,
        name: cur.name,
        priority: cur.priority,
      },
    }),
    {},
  )

  // add summary sheet first, add data later
  workbook.addWorksheet('Summary')

  const generateParticipants = (shirtDetails: EventShirtDetailsProps) => {
    // PARTICIPANTS
    event.categories.items
      ?.sort(prioritySort)
      .forEach((category: Partial<Category>) => {
        sheet = workbook.addWorksheet(cleanName(category.name))
        sheet.columns = [
          ...(category.earlyBirdUntil
            ? [
                {
                  header: 'earlyBird',
                  key: 'earlyBird',
                  width: 10,
                },
              ]
            : []),
          { header: 'firstName', key: 'firstName', width: 25 },
          { header: 'lastName', key: 'lastName', width: 25 },
          { header: 'birthdate', key: 'birthdate', width: 15 },
          { header: 'nationality', key: 'nationality', width: 10 },
          { header: 'gender', key: 'gender', width: 7 },
          {
            header: camelCase(shirtDetails.SHIRT1?.LABEL || 'Shirt'),
            key: 'shirtSize',
            width: 10,
          },
          ...(SHIRT_KEY.SHIRT1 in shirtDetails &&
          'TYPE_OPTIONS' in shirtDetails.SHIRT1
            ? [
                {
                  header: camelCase(
                    `${shirtDetails.SHIRT1?.LABEL} Type` || 'Shirt 1 Type',
                  ),
                  key: 'shirtType',
                  width: 25,
                },
              ]
            : []),
          ...(category.hasJersey
            ? [
                {
                  header: camelCase(shirtDetails.JERSEY?.LABEL || 'Jersey'),
                  key: 'jerseySize',
                  width: 10,
                },
              ]
            : []),
          ...(category.hasShirt2
            ? [
                {
                  header: camelCase(shirtDetails.SHIRT2?.LABEL || 'Shirt 2'),
                  key: 'shirtSize2',
                  width: 25,
                },
              ]
            : []),
          ...(category.hasShirt2 &&
          SHIRT_KEY.SHIRT2 in shirtDetails &&
          'TYPE_OPTIONS' in shirtDetails.SHIRT2
            ? [
                {
                  header: camelCase(
                    `${shirtDetails.SHIRT2?.LABEL} Type` || 'Shirt 2 Type',
                  ),
                  key: 'shirtType2',
                  width: 25,
                },
              ]
            : []),
          ...(event.hasAdditionalId
            ? [
                {
                  header: 'additionalId',
                  key: 'additionalId',
                  width: 25,
                },
              ]
            : []),
          ...(event.firstParticipants
            ? [
                {
                  header: 'firstParticipant',
                  key: 'firstParticipantInfo',
                  width: 25,
                },
              ]
            : []),
          ...(event.hasBibName
            ? [
                {
                  header: 'bibName',
                  key: 'bibName',
                  width: 15,
                },
              ]
            : []),
          {
            header: 'teamName',
            key: 'teamName',
            width: 15,
          },
          ...(category.isTandem
            ? [
                {
                  header: 'tandemName',
                  key: 'tandemName',
                  width: 25,
                },
                {
                  header: 'tandemBirthdate',
                  key: 'tandemBirthdate',
                  width: 20,
                },
                {
                  header: 'tandemShirtSize',
                  key: 'tandemShirtSize',
                  width: 20,
                },
              ]
            : []),
          ...(event.subCategories
            ? [
                {
                  header: 'subCategory',
                  key: 'subCategory',
                  width: 15,
                },
              ]
            : []),
          ...((category.isTriathlon && category.isTriathlon === 1) ||
          (category.teamType && category.teamType === TEAM_TYPES.DUATHLON)
            ? [
                {
                  header: 'relayEvent',
                  key: 'relayEvent',
                  width: 25,
                },
              ]
            : []),
          ...(event.isVirtual
            ? [
                {
                  header: 'distanceCovered',
                  key: 'virtualProgress',
                  width: 20,
                },
              ]
            : []),
          {
            header: 'phoneNumber',
            key: 'phoneNumber',
            width: 25,
          },
          {
            header: 'address',
            key: 'address',
            width: 30,
          },
          {
            header: 'emergencyContactName',
            key: 'emergencyContactName',
            width: 30,
          },
          {
            header: 'emergencyContactPhone',
            key: 'emergencyContactPhone',
            width: 30,
          },
          {
            header: 'userEmail',
            key: 'userEmail',
            width: 20,
          },
          {
            header: 'userPhoneNumber',
            key: 'userPhoneNumber',
            width: 20,
          },
          {
            header: 'paymentId',
            key: 'paymentId',
            width: 20,
          },
          {
            header: 'transactionId',
            key: 'transactionId',
            width: 20,
          },
          {
            header: 'createdAt',
            key: 'createdAt',
            width: 20,
          },
        ]
        sheet.getRow(1).font = headerStyle.font
        sheet.getRow(1).fill = headerStyle.fill

        if (category.id in data) {
          ;(data[category.id] as Participant[])
            ?.sort(dateSort)
            .forEach((item: Participant) => {
              if (item.shirtSize in CONVERT_SIZE)
                item.shirtSize = CONVERT_SIZE[item.shirtSize]

              if (!(item.shirtSize in tshirtCountArr)) {
                tshirtCountArr[item.shirtSize] = {
                  count: 0,
                  name: item.shirtSize,
                  priority: Object.keys(tshirtCountArr).length + 1,
                }
              }
              tshirtCountArr[item.shirtSize].count++

              if (!(item.categoryId in categoryCountArr)) {
                categoryCountArr[item.categoryId] = {
                  count: 0,
                  name: item.categoryId,
                  priority: Object.keys(categoryCountArr).length + 1,
                }
              }
              categoryCountArr[item.categoryId].count++

              if (category.hasShirt2 && item.shirtSize2) {
                if (item.shirtSize2 in CONVERT_SIZE)
                  item.shirtSize2 = CONVERT_SIZE[item.shirtSize2]

                if (!(item.shirtSize2 in tshirt2CountArr)) {
                  tshirt2CountArr[item.shirtSize2] = {
                    count: 0,
                    name: item.shirtSize2,
                    priority: Object.keys(tshirt2CountArr).length + 1,
                  }
                }
                tshirt2CountArr[item.shirtSize2].count++
              }

              if (category.hasJersey && item.jerseySize) {
                if (item.jerseySize in CONVERT_SIZE)
                  item.jerseySize = CONVERT_SIZE[item.jerseySize]

                if (!(item.jerseySize in jerseySizeArr)) {
                  jerseySizeArr[item.jerseySize] = {
                    count: 0,
                    name: item.jerseySize,
                    priority: Object.keys(jerseySizeArr).length + 1,
                  }
                }
                jerseySizeArr[item.jerseySize].count++
              }

              const earlyBird = category.earlyBirdUntil
                ? dayjs(item.createdAt).isSameOrBefore(
                    dayjs(category.earlyBirdUntil),
                  )
                  ? 'Y'
                  : ''
                : ''
              const newRow = sheet.addRow({
                ...item,
                earlyBird,
                userEmail: item?.user?.email || '',
                userPhoneNumber: item?.user?.phoneNumber || '',
                paymentId: item.order.paymentId || '',
                transactionId: item.order.transactionId || '',
              }) as Row
              newRow.font = dataStyle.font
              if (earlyBird === 'Y') newRow.getCell(1).fill = summaryStyle.fill
            })
        }
      })
  }

  const summarySection = (name: string, data: any) => {
    sheet.addRow([])
    sheet = addRow({
      sheet,
      data: ['', name.toUpperCase(), ''],
      style: summaryHeaderStyle,
      exclude: [1],
    })

    let totalCount = 0
    Object.values(data)
      .sort(prioritySort)
      .forEach((item: ItemCount) => {
        totalCount += Number(item.count)
        sheet = addRow({
          sheet,
          data: ['', item.name, item.count],
          style: summaryStyle,
          exclude: [1],
        })
      })

    sheet = addRow({
      sheet,
      data: ['', 'Total', totalCount],
      style: summaryFooterStyle,
      exclude: [1],
    })
  }

  const generateSummary = (shirtDetails: EventShirtDetailsProps) => {
    sheet = workbook.getWorksheet('Summary')
    sheet.getColumn(1).width = 3
    sheet.getColumn(2).width = 25

    // TSHIRT COUNT
    summarySection(
      `${shirtDetails?.SHIRT1?.LABEL || 'SHIRT'} SIZE COUNT`,
      tshirtCountArr,
    )

    // JERSEY COUNT
    if (hasShirt2)
      summarySection(
        `${shirtDetails?.SHIRT2?.LABEL || 'SHIRT 2'} SIZE COUNT`,
        tshirt2CountArr,
      )

    // JERSEY COUNT
    if (hasJersey)
      summarySection(
        `${shirtDetails?.JERSEY?.LABEL || 'JERSEY'} SIZE COUNT`,
        jerseySizeArr,
      )

    // CATEGORY COUNT
    summarySection('PARTICIPANT COUNT', categoryCountArr)
  }

  const shirtDetails = {
    ...EVENT_SHIRT_DETAILS,
    ...(event.shirtDetails && {
      ...JSON.parse(JSON.parse(event.shirtDetails as string)),
    }),
  }

  generateParticipants(shirtDetails)
  generateSummary(shirtDetails)
  return workbook
}
